
import { Task, TimeUnit } from '../types';

/**
 * Normalizes duration to HOURS.
 * Assumptions:
 * 1 Day = 8 Working Hours
 * 1 Week = 40 Working Hours
 */
const toHours = (duration: number, unit: TimeUnit): number => {
  switch (unit) {
    case TimeUnit.QUARTER: return duration * 0.25;
    case TimeUnit.HOUR: return duration * 1.0;
    case TimeUnit.DAY: return duration * 8.0;
    case TimeUnit.WEEK: return duration * 40.0;
    default: return duration;
  }
};

/**
 * Calculates Critical Path Method (CPM) metrics:
 * Early Start/Finish, Late Start/Finish, Slack, and Critical Path flags.
 * All computed values are in HOURS.
 */
export const calculateCPM = (tasks: Task[]): Task[] => {
  if (tasks.length === 0) return [];

  const taskMap = new Map<string, Task>();
  // Clone tasks to avoid mutating state directly during calculation
  const calculatedTasks = tasks.map(t => ({ ...t }));
  calculatedTasks.forEach(t => taskMap.set(t.id, t));

  // --- Forward Pass (Early Start / Early Finish) ---
  let hasChanges = true;
  while (hasChanges) {
    hasChanges = false;
    calculatedTasks.forEach(task => {
      let maxDependencyEnd = 0;
      
      task.dependencies.forEach(depId => {
        const dep = taskMap.get(depId);
        if (dep && dep.earlyFinish !== undefined) {
          if (dep.earlyFinish > maxDependencyEnd) {
            maxDependencyEnd = dep.earlyFinish;
          }
        }
      });

      const durationHours = toHours(task.duration, task.unit);
      const newEarlyStart = maxDependencyEnd;
      const newEarlyFinish = newEarlyStart + durationHours;

      if (task.earlyStart !== newEarlyStart || task.earlyFinish !== newEarlyFinish) {
        task.earlyStart = newEarlyStart;
        task.earlyFinish = newEarlyFinish;
        hasChanges = true;
      }
    });
  }

  // --- Backward Pass (Late Start / Late Finish) ---
  const projectDuration = Math.max(...calculatedTasks.map(t => t.earlyFinish || 0));

  calculatedTasks.forEach(t => {
    const durationHours = toHours(t.duration, t.unit);
    t.lateFinish = projectDuration;
    t.lateStart = projectDuration - durationHours;
  });

  hasChanges = true;
  while (hasChanges) {
    hasChanges = false;
    calculatedTasks.forEach(task => {
      // Find tasks that depend on the current task
      const successors = calculatedTasks.filter(t => t.dependencies.includes(task.id));
      const durationHours = toHours(task.duration, task.unit);

      if (successors.length > 0) {
        // Late Finish is the MINIMUM of successors' Late Starts
        const minSuccessorStart = Math.min(...successors.map(s => s.lateStart || projectDuration));
        
        if (task.lateFinish !== minSuccessorStart) {
          task.lateFinish = minSuccessorStart;
          task.lateStart = (task.lateFinish || 0) - durationHours;
          hasChanges = true;
        }
      }
    });
  }

  // --- Slack & Critical Path ---
  calculatedTasks.forEach(task => {
    task.slack = (task.lateStart || 0) - (task.earlyStart || 0);
    // Floating point precision tolerance
    task.isCritical = (task.slack || 0) < 0.01; 
  });

  return calculatedTasks.sort((a, b) => (a.earlyStart || 0) - (b.earlyStart || 0));
};
